import { CardPermission, CardStatus } from "@src/gql/graphql";
import { z } from "zod";

/** Normalize API / Yjs status strings into CardStatus enum values. */
export function normalizeCardStatusInput(value: unknown): unknown {
	if (value == null || value === "") return undefined;
	if (typeof value !== "string") return value;
	const upper = value.toUpperCase();
	if (
		upper === CardStatus.Active ||
		upper === CardStatus.Archived ||
		upper === CardStatus.Deleted
	) {
		return upper;
	}
	return value;
}

export const cardStatusSchema = z.preprocess(
	normalizeCardStatusInput,
	z.nativeEnum(CardStatus),
);

export const optionalCardStatusSchema = z.preprocess(
	normalizeCardStatusInput,
	z.nativeEnum(CardStatus).nullish(),
);

export const cardPermissionSchema = z.nativeEnum(CardPermission);

export const collaboratorSchema = z.object({
	userId: z.string().nullish(),
	userEmail: z.string(),
	permission: cardPermissionSchema,
});

export const collaborationSchema = z.object({
	isShared: z.boolean(),
	collaborators: z.array(collaboratorSchema),
});

export const publicationSchema = z.object({
	isPublished: z.boolean(),
	key: z.string().nullish(),
	shareKeyPermission: cardPermissionSchema.nullish(),
});

export const cardWhiteboardOccurrenceSchema = z.object({
	id: z.string(),
	knownRanklist: z.string().nullish(),
	shapeIds: z.array(z.string()),
});

export const libraryCardSchema = z.object({
	createdAt: z.string().nullish(),
	id: z.string(),
	isPublished: z.boolean().nullish(),
	name: z.string().nullish(),
	ownerId: z.string().nullish(),
	position: z.union([z.string(), z.number()]).nullish(),
	shareKey: z.string().nullish(),
	status: optionalCardStatusSchema,
	streamPosition: z.union([z.string(), z.number()]).nullish(),
});

export const singleCardSchema = libraryCardSchema.extend({
	ownerName: z.string().nullish(),
	ownerAvatarUrl: z.string().nullish(),
	whiteboards: z.array(cardWhiteboardOccurrenceSchema).nullish(),
	collaboration: collaborationSchema.nullish(),
	publication: publicationSchema.nullish(),
});

export type LibraryListCardNode = z.infer<typeof libraryCardSchema>;
export type LibraryCardNode = z.infer<typeof singleCardSchema>;
